import React, { FC, useState } from "react";
import { Td, Tr, Icon, IconButton } from "@chakra-ui/react";
import { colours } from "@/utils/themeUtils";
import { ClassroomSessionTimeSelector } from "../../ClassroomSessionTimeSelector";
import ClassroomSessionSchedulerDaySelector from "./ClassroomSessionScheduleDaySelect";

interface Value {
	id: string;
	day?: string;
	endTime?: number | null;
	startTime?: number | null;
}

interface Props {
	handleRemove: (id: string) => void;
	onChange: (value: Value, error: boolean) => void;
	value: Value;
	isNew: boolean;
}

const ClassroomSessionScheduleRow: FC<Props> = (props) => {
	const [startTime, setStartTime] = useState<number | null>(props.value.startTime ?? null);
	const [endTime, setEndTime] = useState<number | null>(props.value.endTime ?? null);
	const [startTimeError, setStartTimeError] = useState<string | null>(null);
	const [endTimeError, setEndTimeError] = useState<string | null>(null);

	const handleChangeDay = (value: string) => {
		const newSchedule = {
			...props.value,
			day: value,
		};
		props.onChange(newSchedule, startTimeError !== null || endTimeError !== null);
	};

	const handleChangeStartTime = (value: number | null, error: string | null) => {
		setStartTime(value);
		setStartTimeError(error);
		const newSchedule = {
			...props.value,
			startTime: value,
		};
		props.onChange(newSchedule, error !== null || endTimeError !== null);
	};

	const handleChangeEndTime = (value: number | null, error: string | null) => {
		setEndTime(value);
		setEndTimeError(error);
		const newSchedule = {
			...props.value,
			endTime: value,
		};
		props.onChange(newSchedule, startTimeError !== null || error !== null);
	};

	const handleRemove = () => {
		props.handleRemove(props.value.id);
	};

	const handleHighlightStyle = () => {
		if (!!startTimeError || !!endTimeError) {
			return { background: colours.danger.background.weaker };
		}
		if (props.isNew) {
			return { background: colours.highlight["new-row-bg"] };
		}
		return undefined;
	};

	return (
		<Tr style={handleHighlightStyle()} data-testid="classroom-session-scheduler-session-row">
			<Td verticalAlign="baseline">
				<ClassroomSessionSchedulerDaySelector value={props.value.day} onChange={handleChangeDay} />
			</Td>
			<Td verticalAlign="baseline">
				<ClassroomSessionTimeSelector
					preselected={startTime}
					onChange={handleChangeStartTime}
					options={{ validateFuture: false, autoFocus: false, maxTime: endTime }}
				/>
			</Td>
			<Td verticalAlign="baseline">
				<ClassroomSessionTimeSelector
					preselected={endTime}
					onChange={handleChangeEndTime}
					options={{ validateFuture: false, autoFocus: false, minTime: startTime }}
				/>
			</Td>
			<Td verticalAlign="baseline">
				<IconButton
					variant="unstyled"
					_hover={{
						background: "transparent",
					}}
					aria-label="Remove session"
					icon={<Icon className="fa-times fa-regular" boxSize="20px" color={colours.danger.icon} />}
					onClick={handleRemove}
					data-testid="close-icon"
				/>
			</Td>
		</Tr>
	);
};

export default ClassroomSessionScheduleRow;
